import { useEffect, useRef } from "react";
import { Box, CircularProgress, InputBase } from "@mui/material";
import { Search as SearchIcon, People as PeopleIcon } from "@mui/icons-material";
import { t } from "./coachTokens";

const DashboardTable = ({ count, search, onSearchChange, extraFilters, filterChips, loading, hasMore, loadingMore, onReachEnd, children }) => {
  const sentinelRef = useRef(null);
  const rootRef = useRef(null);

  // Trigger onReachEnd whenever the bottom sentinel scrolls into view, the list
  // has more rows to load, and we're not already fetching.
  //
  // `loading` must be in the deps AND in the guard: this effect re-creates the
  // observer on every hasMore/loadingMore flip, and a fresh IntersectionObserver
  // fires immediately for an already-intersecting target. Reading a stale
  // loading===false from an older closure would bump the page against an in-flight
  // request, appending the same slice twice. (onReachEnd is also ref-guarded.)
  useEffect(() => {
    if (!hasMore || !onReachEnd) return;
    const target = sentinelRef.current;
    const root = rootRef.current;
    if (!target || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMore && !loading) onReachEnd();
      },
      { root, rootMargin: "120px 0px" }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [hasMore, loadingMore, loading, onReachEnd]);

  return (
    <Box
      sx={{
        background: t.white,
        border: `1px solid ${t.border}`,
        borderRadius: t.r,
        boxShadow: t.sh,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "7px",
          padding: "10px 14px",
          borderBottom: `1px solid ${t.border}`,
          background: t.bg,
          flexWrap: "wrap",
          position: "relative",
          zIndex: 2,
          borderTopLeftRadius: t.r,
          borderTopRightRadius: t.r,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "12px",
            fontWeight: 700,
            color: t.greenText,
            background: t.greenLight,
            border: "1px solid rgba(1,187,167,.2)",
            padding: "4px 12px",
            borderRadius: "20px",
            whiteSpace: "nowrap",
          }}
        >
          <PeopleIcon sx={{ fontSize: 14 }} />
          {count}
        </Box>
        <Box sx={{ position: "relative" }}>
          <SearchIcon
            sx={{
              position: "absolute",
              left: 9,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 14,
              color: t.t3,
              pointerEvents: "none",
            }}
          />
          <InputBase
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="חיפוש..."
            sx={{
              width: 180,
              padding: "6px 10px 6px 30px",
              border: `1.5px solid ${t.border}`,
              borderRadius: t.rs,
              background: t.white,
              color: t.text,
              fontSize: "12px",
              "& input": { padding: 0 },
              "&:focus-within": { borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)" },
            }}
          />
        </Box>
        {extraFilters}
        <Box sx={{ flex: 1 }} />
        {filterChips}
      </Box>
      <Box
        ref={rootRef}
        // Cap visible area so ~6 rows show before the user has to scroll. Each
        // row is roughly 60px (12px×2 padding + 36px content); plus the sticky
        // header row.
        sx={{ position: "relative", overflowY: "auto", maxHeight: 60 * 6 + 48, borderBottomLeftRadius: t.r, borderBottomRightRadius: t.r }}
      >
        {children}
        {/* Sentinel — observed by the IntersectionObserver above to trigger
            the next page when scrolled into view. */}
        {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
            <CircularProgress size={18} sx={{ color: t.t3 }} />
          </Box>
        )}
        {/* Overlay shown when reloading from page 0 (filter/search/sort change).
            Sits inside the table only — no full-screen flash. */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,.7)",
              backdropFilter: "blur(1px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
            }}
          >
            <CircularProgress size={26} sx={{ color: t.green }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DashboardTable;
